import { message } from "antd";
import PropTypes from "prop-types";
// eslint-disable-next-line no-unused-vars
import React, { forwardRef, useState } from "react";

import Label from "metabase/common/components/type/Label";
import { getSpreadsheetCompatibleExportData } from "metabase/jerry/Common/export-data";
import { trackEvent } from "metabase/jerry/Common/jerry-utils";
import { FormButton } from "metabase/jerry/style/FormButton.styled";
import { openInBlankWindow } from "metabase/lib/dom";
import { Icon } from "metabase/ui";

function trackExportEvent(card, user, response) {
  const event = {
    eventCategory: "Metabase",
    eventAction: "Frontend",
    eventLabel: "export_spreadsheet",
  };
  const meta = {
    user_info: user,
    card: card,
    response: response,
  };
  trackEvent(event, meta);
}

const ExportSpreadsheet = forwardRef(
  (
    {
      card,
      result,
      user,
      isShowingRawTable,
      rawSeries,
      visualizationSettings,
      onDownloadStart,
      onDownloadResolved,
      onDownloadRejected,
      ...props
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleExport = async () => {
      setIsLoading(true);
      if (typeof onDownloadStart === "function") {
        onDownloadStart();
      }

      try {
        const sourceData = result?.data;
        const exportData = getSpreadsheetCompatibleExportData({
          isShowingRawTable,
          result,
          visualizationSettings,
          rawSeries,
        });
        const dataForPayload = {};

        if (sourceData !== undefined && sourceData !== null) {
          dataForPayload.data = {
            ...sourceData,
            ...exportData,
          };
        }
        dataForPayload.user = user;
        const cardId = card?.id;
        if (cardId !== undefined) {
          dataForPayload.card = card;
        } else {
          dataForPayload.card = null;
        }

        const response = await fetch(`/api/jerry/extend`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            call: "post",
            service_name: "spreadsheet-writer-service",
            body: dataForPayload,
            timeout: 120000,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            message: "Unable to parse error response.",
          }));
          throw new Error(
            errorData.message ||
              `Export failed with HTTP status: ${response.status}`,
          );
        }

        const responseData = await response.json();

        trackExportEvent(card, user, responseData);

        const redirectUrl =
          responseData?.data?.worksheet?.url ??
          responseData?.data?.spreadsheet?.url;
        if (redirectUrl) {
          openInBlankWindow(redirectUrl);
          if (typeof onDownloadResolved === "function") {
            onDownloadResolved(responseData);
          }
          message.info("Export completed successfully");
        } else {
          throw new Error(
            "Export succeeded, but the response did not contain a URL.",
          );
        }

        if (typeof onDownloadResolved === "function") {
          onDownloadResolved(responseData);
        }
      } catch (error) {
        console.error("Export failed:", error);
        message.error(error.message);
        if (typeof onDownloadRejected === "function") {
          onDownloadRejected(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div style={{ width: "100%" }} ref={ref}>
        <FormButton
          className="hover-parent hover--inherit"
          onClick={handleExport}
          disabled={isLoading}
          {...props}
        >
          <Icon
            name="google_sheet"
            size={38}
            style={{ marginLeft: "-3px", marginRight: "6px", color: "green" }}
          />
          <Label my={0} style={{ marginTop: "5px" }}>
            {isLoading ? "Exporting..." : "One-time Export"}
          </Label>
        </FormButton>
      </div>
    );
  },
);

ExportSpreadsheet.displayName = "ExportSpreadsheet";

ExportSpreadsheet.propTypes = {
  card: PropTypes.object,
  result: PropTypes.object,
  user: PropTypes.object,
  isShowingRawTable: PropTypes.bool,
  rawSeries: PropTypes.array,
  visualizationSettings: PropTypes.object,
  onDownloadStart: PropTypes.func,
  onDownloadResolved: PropTypes.func,
  onDownloadRejected: PropTypes.func,
};

export default ExportSpreadsheet;
